import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { DateInput } from '@orbind/ui'
import { StatusPill, TONE_COLOR } from './bits.tsx'
import './wi.css'

/**
 * Popup preso a um ícone do cronograma, desenhado FORA da grade (2026-08-31).
 *
 * A célula da árvore é `sticky` com z-index própria e a grade rola nos dois
 * eixos: qualquer menu posicionado dentro dela era recortado pelo scroll e
 * pintado por baixo das linhas seguintes. Aqui o menu vai para o `body`, em
 * posição fixa calculada a partir do retângulo do ícone, virando para cima
 * quando não cabe embaixo e sem escapar pelas laterais.
 */
function PopFlutuante({
  anc,
  classe,
  onFechar,
  children,
}: {
  anc: DOMRect
  classe: string
  onFechar: () => void
  children: React.ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const m = 8
    // alinhado pela DIREITA do ícone (os ícones ficam no fim da linha)
    let left = anc.right - r.width
    let top = anc.bottom + 6
    if (top + r.height > window.innerHeight - m) top = anc.top - r.height - 6
    left = Math.min(Math.max(m, left), Math.max(m, window.innerWidth - r.width - m))
    top = Math.min(Math.max(m, top), Math.max(m, window.innerHeight - r.height - m))
    setPos({ left, top })
  }, [anc])

  useEffect(() => {
    // fecha ao clicar fora, ao rolar a grade e no Esc
    const fora = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onFechar()
    }
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar()
    }
    document.addEventListener('pointerdown', fora, true)
    window.addEventListener('keydown', tecla)
    window.addEventListener('scroll', onFechar, true)
    window.addEventListener('resize', onFechar)
    return () => {
      document.removeEventListener('pointerdown', fora, true)
      window.removeEventListener('keydown', tecla)
      window.removeEventListener('scroll', onFechar, true)
      window.removeEventListener('resize', onFechar)
    }
  }, [onFechar])

  return createPortal(
    <div
      ref={ref}
      className={`${classe} wic__pop`}
      style={{ left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden' }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  )
}

/**
 * Visão de um Item de Trabalho que TEM FILHOS (pedido 2026-08-26): em vez do
 * detalhe, a área de trabalho mostra a lista de todos os descendentes em árvore
 * + Gantt, como no projeto. Clicar num filho abre a edição dele; fechar volta
 * para cá (pela trilha do explorer).
 *
 * Os CHECKLISTS do item agrupam o cronograma (2026-08-30): cada checklist vira
 * uma faixa com as atividades dele dentro, e a barra da faixa vai da primeira
 * data até a última das atividades — é o tempo do checklist inteiro. Linhas que
 * não viraram Item de Trabalho entram na mesma lista, com data própria.
 */

const DIA_MS = 86_400_000
const DIA_W = 28

export type LinhaItem = {
  id: string
  code: string
  title: string
  /** Ícone do tipo, mostrado ao lado do item na linha. */
  typeIcon?: string | null
  /** Cor e nome do tipo — o ícone usa a cor, e o nome vira o hint. */
  typeTone?: string | null
  typeLabel?: string | null
  startAt: string | null
  dueAt: string | null
  parentWorkItemId: string | null
  status: { tone: string; isTerminal: boolean }
}

/** Linha de checklist como o detalhe do item devolve. */
export type LinhaChecklist = {
  id: string
  text: string
  isDone: boolean
  sortOrder: number
  startAt: string | null
  dueAt: string | null
  linked: { id: string; code: string; title: string; startAt: string | null; dueAt: string | null } | null
}
export type ChecklistDoItem = {
  id: string
  /** Item dono do checklist — é o que permite descer níveis. */
  workItemId: string
  name: string
  /** Prazo combinado do próprio checklist, independente das atividades. */
  startAt: string | null
  dueAt: string | null
  items: LinhaChecklist[]
}

/** Uma linha desenhada no cronograma. */
type Linha = {
  chave: string
  /** Nível de indentação. */
  depth: number
  code: string | null
  titulo: string
  ini: string | null
  fim: string | null
  tone: string
  concluido: boolean
  /** Item de Trabalho por trás da linha (abre ao clicar). */
  itemId: string | null
  /** Linha de checklist por trás (recebe as datas quando não há item). */
  checklistItemId: string | null
  /** Checklist por trás da linha de resumo (recebe o prazo combinado). */
  checklistId?: string | null
  /** Cabeçalho de checklist: barra resumo. */
  resumo?: boolean
  /** Chave do nó de quem esta linha desce — é o que permite recolher o galho. */
  paiChave: string | null
  /** Ícone do tipo do item (só nas linhas que são item). */
  icone?: string | null
  iconeTone?: string | null
  tipoNome?: string | null
}

const diaDe = (v: string) => Math.floor(new Date(v).getTime() / DIA_MS)
const menor = (a: string | null, b: string | null) => (a && b ? (diaDe(a) <= diaDe(b) ? a : b) : (a ?? b))
const maior = (a: string | null, b: string | null) => (a && b ? (diaDe(a) >= diaDe(b) ? a : b) : (a ?? b))

export function WorkItemChildrenView({
  pai,
  todos,
  checklists = [],
  onAbrir,
  onEditarPai,
  onFechar,
  onDatasDaLinha,
  onDatasDoItem,
  onDatasDoChecklist,
  statusOpcoes = [],
  etapaOpcoes = [],
  onTrocarStatus,
  onTrocarEtapa,
}: {
  pai: LinhaItem
  todos: LinhaItem[]
  /** Checklists do item pai — agrupam o cronograma. */
  checklists?: ChecklistDoItem[]
  onAbrir: (id: string) => void
  onEditarPai: () => void
  onFechar: () => void
  /** Grava início/prazo numa LINHA de checklist (a que não virou item). */
  onDatasDaLinha?: (checklistItemId: string, datas: { startAt: string | null; dueAt: string | null }) => void
  /** Grava início/prazo num Item de Trabalho. */
  onDatasDoItem?: (itemId: string, datas: { startAt: string | null; dueAt: string | null }) => void
  /** Grava o prazo combinado do próprio checklist. */
  onDatasDoChecklist?: (checklistId: string, datas: { startAt: string | null; dueAt: string | null }) => void
  /**
   * Catálogos para mexer no item SEM abrir ele (2026-08-30): a bolinha troca o
   * status e o ícone da etapa troca a etapa, ali mesmo na linha.
   */
  statusOpcoes?: { id: string; label: string; tone: string; isTerminal: boolean }[]
  etapaOpcoes?: { id: string; label: string }[]
  onTrocarStatus?: (itemId: string, statusId: string) => void
  onTrocarEtapa?: (itemId: string, flowStepId: string) => void
}) {
  /**
   * Linha com o editor de datas aberto, e para que lado ele abre.
   *
   * `acima` evita que o editor da última linha nasça fora da área rolável do
   * cronograma — de onde ele não voltaria nem rolando.
   */
  const [editando, setEditando] = useState<{ chave: string; anc: DOMRect } | null>(null)
  /** Linha com o menu de status ou de etapa aberto. */
  const [menuDe, setMenuDe] = useState<{ chave: string; qual: 'status' | 'etapa'; anc: DOMRect } | null>(null)
  /**
   * Largura da coluna dos itens, arrastável pela borda (2026-08-30). Era fixa em
   * 320px e nomes longos ficavam cortados; a escolha fica lembrada.
   */
  const [colLargura, setColLargura] = useState(() => {
    const salvo = Number(localStorage.getItem('orbind.gantt.col'))
    return Number.isFinite(salvo) && salvo >= 200 ? salvo : 320
  })
  useEffect(() => {
    localStorage.setItem('orbind.gantt.col', String(colLargura))
  }, [colLargura])
  const arrasteCol = useRef<{ x: number; larg: number } | null>(null)
  const limitaCol = (px: number) => Math.round(Math.min(Math.max(200, px), Math.max(360, window.innerWidth * 0.6)))

  /** Nós recolhidos (checklist ou item com filhos). */
  const [recolhidos, setRecolhidos] = useState<ReadonlySet<string>>(new Set())
  const alternarNo = (chave: string) =>
    setRecolhidos((atual) => {
      const proximo = new Set(atual)
      if (proximo.has(chave)) proximo.delete(chave)
      else proximo.add(chave)
      return proximo
    })

  const porId = useMemo(() => new Map(todos.map((i) => [i.id, i])), [todos])

  /**
   * Monta as linhas do cronograma DESCENDO a árvore inteira.
   *
   * Cada item pode ter checklists, e cada linha de checklist pode ser outro Item
   * de Trabalho — que por sua vez tem os seus checklists. O desenho acompanha
   * isso em qualquer profundidade: checklist → atividade → checklist da
   * atividade → ... Sem isso, só o primeiro nível apareceria.
   *
   * A barra de um checklist vai da primeira à última data do que está DENTRO
   * dele, incluindo o que vem dos níveis de baixo — é o tempo do bloco inteiro.
   */
  const linhas = useMemo<Linha[]>(() => {
    const out: Linha[] = []
    const listasDoItem = new Map<string, ChecklistDoItem[]>()
    for (const cl of checklists) {
      listasDoItem.set(cl.workItemId, [...(listasDoItem.get(cl.workItemId) ?? []), cl])
    }
    const filhosDe = new Map<string, LinhaItem[]>()
    for (const item of todos) {
      if (!item.parentWorkItemId) continue
      filhosDe.set(item.parentWorkItemId, [...(filhosDe.get(item.parentWorkItemId) ?? []), item])
    }
    /** Ciclo por dado torto não pode virar recursão infinita. */
    const visitados = new Set<string>()

    /** Menor início e maior fim de um conjunto de linhas. */
    const extremos = (ls: Linha[]) => {
      const is = ls.map((l) => l.ini).filter((v): v is string => Boolean(v))
      const fs2 = ls.map((l) => l.fim).filter((v): v is string => Boolean(v))
      return {
        ini: is.length ? is.reduce((a, b) => (diaDe(a) <= diaDe(b) ? a : b)) : null,
        fim: fs2.length ? fs2.reduce((a, b) => (diaDe(a) >= diaDe(b) ? a : b)) : null,
      }
    }

    /** Desenha o conteúdo de um item: seus checklists e seus filhos soltos. */
    const doItem = (itemId: string, depth: number, paiChave: string | null): Linha[] => {
      if (visitados.has(itemId)) return []
      visitados.add(itemId)
      const geradas: Linha[] = []
      const usados = new Set<string>()

      for (const cl of listasDoItem.get(itemId) ?? []) {
        const chaveCl = `cl:${cl.id}`
        const filhas: Linha[] = []
        for (const li of [...cl.items].sort((a, b) => a.sortOrder - b.sortOrder)) {
          const item = li.linked ? porId.get(li.linked.id) : undefined
          if (li.linked) usados.add(li.linked.id)
          const chaveLi = `li:${li.id}`
          // o que estiver pendurado nesta atividade vem antes, para a barra dela
          // poder considerar os níveis de baixo
          const netos = li.linked ? doItem(li.linked.id, depth + 2, chaveLi) : []
          const propria = {
            ini: item?.startAt ?? li.linked?.startAt ?? li.startAt ?? null,
            fim: item?.dueAt ?? li.linked?.dueAt ?? li.dueAt ?? null,
          }
          const dosNetos = extremos(netos)
          const ini = propria.ini ?? dosNetos.ini
          const fim = propria.fim ?? dosNetos.fim
          filhas.push({
            chave: chaveLi,
            depth: depth + 1,
            code: li.linked?.code ?? null,
            titulo: li.linked?.title ?? li.text,
            ini: ini ?? fim,
            fim: fim ?? ini,
            tone: item?.status.tone ?? (li.isDone ? 'success' : 'neutral'),
            concluido: item?.status.isTerminal ?? li.isDone,
            itemId: li.linked?.id ?? null,
            checklistItemId: li.linked ? null : li.id,
            paiChave: chaveCl,
            icone: item?.typeIcon ?? null,
            iconeTone: item?.typeTone ?? null,
            tipoNome: item?.typeLabel ?? null,
          })
          filhas.push(...netos)
        }
        /**
         * O checklist tem prazo próprio E as atividades têm o delas: vale o
         * MENOR início e o MAIOR fim entre os dois. Assim uma atividade fora do
         * combinado estica a barra em vez de ficar de fora dela, e um checklist
         * sem atividade nenhuma ainda aparece no cronograma.
         */
        const dasFilhas = extremos(filhas)
        const ini = menor(cl.startAt, dasFilhas.ini)
        const fim = maior(cl.dueAt, dasFilhas.fim)
        geradas.push({
          chave: chaveCl,
          depth,
          code: null,
          titulo: cl.name,
          ini: ini ?? fim,
          fim: fim ?? ini,
          tone: filhas.length > 0 && filhas.every((f) => f.concluido) ? 'success' : 'info',
          concluido: filhas.length > 0 && filhas.every((f) => f.concluido),
          itemId: null,
          checklistItemId: null,
          checklistId: cl.id,
          resumo: true,
          paiChave,
        })
        geradas.push(...filhas)
      }

      // filhos que não estão em nenhum checklist deste item
      for (const filho of (filhosDe.get(itemId) ?? []).sort((a, b) => a.code.localeCompare(b.code))) {
        if (usados.has(filho.id)) continue
        const chaveWi = `wi:${filho.id}`
        const netos = doItem(filho.id, depth + 1, chaveWi)
        const dosNetos = extremos(netos)
        const ini = filho.startAt ?? filho.dueAt ?? dosNetos.ini
        const fim = filho.dueAt ?? filho.startAt ?? dosNetos.fim
        geradas.push({
          chave: chaveWi,
          depth,
          code: filho.code,
          titulo: filho.title,
          ini: ini ?? fim,
          fim: fim ?? ini,
          tone: filho.status.tone,
          concluido: filho.status.isTerminal,
          itemId: filho.id,
          checklistItemId: null,
          paiChave,
          icone: filho.typeIcon ?? null,
          iconeTone: filho.typeTone ?? null,
          tipoNome: filho.typeLabel ?? null,
        })
        geradas.push(...netos)
      }
      return geradas
    }

    out.push(...doItem(pai.id, 0, null))
    return out
  }, [todos, checklists, pai.id, porId])

  const faixa = useMemo(() => {
    const datas = linhas.filter((l): l is Linha & { ini: string; fim: string } => Boolean(l.ini && l.fim))
    if (datas.length === 0) return null
    const min = Math.min(...datas.map((d) => diaDe(d.ini))) - 3
    const max = Math.max(...datas.map((d) => diaDe(d.fim))) + 3
    const dias: Date[] = []
    for (let d = min; d <= max; d++) dias.push(new Date(d * DIA_MS))
    return { min, dias }
  }, [linhas])

  const quantasAtividades = linhas.filter((l) => !l.resumo).length

  /** Nós que têm algo pendurado — só eles ganham a setinha de recolher. */
  const comFilhos = useMemo(
    () => new Set(linhas.map((l) => l.paiChave).filter((c): c is string => Boolean(c))),
    [linhas],
  )

  /**
   * Aplica o recolhimento: some com a subárvore inteira de um nó fechado, não só
   * com os filhos diretos. Como as linhas já vêm na ordem de leitura, basta ir
   * marcando os galhos escondidos ao longo da lista.
   */
  const visiveis = useMemo(() => {
    const escondidos = new Set<string>()
    const out: Linha[] = []
    for (const l of linhas) {
      if (l.paiChave && escondidos.has(l.paiChave)) {
        escondidos.add(l.chave)
        continue
      }
      out.push(l)
      if (recolhidos.has(l.chave)) escondidos.add(l.chave)
    }
    return out
  }, [linhas, recolhidos])

  /** Grava as datas de uma linha, no item ou na própria linha do checklist. */
  const gravar = (linha: Linha, datas: { startAt: string | null; dueAt: string | null }) => {
    if (linha.itemId) onDatasDoItem?.(linha.itemId, datas)
    else if (linha.checklistItemId) onDatasDaLinha?.(linha.checklistItemId, datas)
    else if (linha.checklistId) onDatasDoChecklist?.(linha.checklistId, datas)
  }

  return (
    <div className="wi-detail pj__view" style={{ ['--pj-col' as string]: `${colLargura}px` }}>
      <header className="wic__head">
        <button type="button" className="wic__close" aria-label="Fechar" title="Fechar" onClick={onFechar}>
          <i className="fas fa-arrow-left" aria-hidden />
        </button>
        <div className="wic__titulo">
          <span className="wic__codigo">{pai.code}</span>
        </div>
        <button type="button" className="wic__editar" onClick={onEditarPai}>
          <i className="fas fa-pen" aria-hidden style={{ marginRight: 6 }} />
          Editar este item
        </button>
      </header>

      <div className="pj__gantt">
        <div className="pj__linha">
          <div className="pj__arvorecel pj__arvorecel--cab">
            {pai.typeIcon ? (
              <i
                className={`${pai.typeIcon} pj__tipoico`}
                style={{ color: TONE_COLOR[pai.typeTone as keyof typeof TONE_COLOR] ?? undefined }}
                title={pai.typeLabel ?? undefined}
                aria-label={pai.typeLabel ?? undefined}
              />
            ) : null}
            <span className="pj__cabnome" title={`${pai.code} · ${pai.title}`}>
              {pai.title}
            </span>
            <span className="pj__cabcont">{quantasAtividades}</span>
            <span
              className="pj__colgrip"
              role="separator"
              aria-orientation="vertical"
              aria-label="Redimensionar a coluna dos itens"
              title="Arraste para redimensionar"
              onPointerDown={(e) => {
                arrasteCol.current = { x: e.clientX, larg: colLargura }
                ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
              }}
              onPointerMove={(e) => {
                if (!arrasteCol.current) return
                setColLargura(limitaCol(arrasteCol.current.larg + (e.clientX - arrasteCol.current.x)))
              }}
              onPointerUp={(e) => {
                arrasteCol.current = null
                ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
              }}
            />
          </div>
          <div className="pj__tempo" style={{ height: 40 }}>
            {faixa ? (
              faixa.dias.map((d, i) => {
                const fds = d.getDay() === 0 || d.getDay() === 6
                const mostraMes = i === 0 || d.getDate() === 1
                return (
                  <span key={i} className={`pj__dia pj__dia--cab${fds ? ' pj__dia--fds' : ''}`} style={{ width: DIA_W }}>
                    {mostraMes ? (
                      <span className="pj__mes">{new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(d).replace('.', '')}</span>
                    ) : null}
                    <span>{d.getDate()}</span>
                  </span>
                )
              })
            ) : (
              <span className="pj__meta" style={{ padding: '0 14px' }}>
                Sem datas ainda — use o ícone de calendário na linha para definir início e prazo.
              </span>
            )}
          </div>
        </div>

        {visiveis.map((linha) => {
          const cor = TONE_COLOR[linha.tone as keyof typeof TONE_COLOR] ?? 'var(--ob-primary)'
          let barra: { left: number; width: number } | null = null
          if (faixa && linha.ini && linha.fim) {
            const d0 = diaDe(linha.ini)
            const d1 = diaDe(linha.fim)
            barra = { left: (d0 - faixa.min) * DIA_W + 2, width: Math.max(DIA_W - 4, (d1 - d0 + 1) * DIA_W - 4) }
          }
          const hoje = faixa ? (Math.floor(Date.now() / DIA_MS) - faixa.min) * DIA_W : null
          const podeAbrir = linha.itemId !== null
          const podeDatar = (linha.itemId ?? linha.checklistItemId ?? linha.checklistId) != null
          return (
            <div
              key={linha.chave}
              className={`pj__linha wic__linha${linha.resumo ? ' wic__linha--resumo' : ''}`}
              role={podeAbrir ? 'button' : undefined}
              tabIndex={podeAbrir ? 0 : undefined}
              onClick={() => (podeAbrir ? onAbrir(linha.itemId!) : undefined)}
              onKeyDown={(e) => {
                if (podeAbrir && e.key === 'Enter') onAbrir(linha.itemId!)
              }}
            >
              <div
                className="pj__arvorecel"
                style={{ paddingLeft: 10 + linha.depth * 18 }}
              >
                {comFilhos.has(linha.chave) ? (
                  <button
                    type="button"
                    className="wic__no"
                    aria-expanded={!recolhidos.has(linha.chave)}
                    aria-label={recolhidos.has(linha.chave) ? 'Expandir' : 'Recolher'}
                    title={recolhidos.has(linha.chave) ? 'Expandir' : 'Recolher'}
                    onClick={(e) => {
                      e.stopPropagation()
                      alternarNo(linha.chave)
                    }}
                  >
                    <i className={`fas fa-chevron-${recolhidos.has(linha.chave) ? 'right' : 'down'}`} aria-hidden />
                  </button>
                ) : (
                  <span className="wic__no wic__no--vazio" aria-hidden />
                )}
                {linha.resumo ? (
                  <i className="fas fa-list-ul wic__icochecklist" aria-hidden />
                ) : linha.depth > 0 ? (
                  <i className="fas fa-level-up-alt fa-rotate-90 pj__galho" aria-hidden />
                ) : null}
                {linha.icone ? (
                  <i
                    className={`${linha.icone} pj__tipoico`}
                    style={{ color: TONE_COLOR[linha.iconeTone as keyof typeof TONE_COLOR] ?? undefined }}
                    title={linha.tipoNome ?? undefined}
                    aria-label={linha.tipoNome ?? undefined}
                  />
                ) : null}
                {linha.code ? <span className="pj__codigo">{linha.code}</span> : null}
                <span className="pj__nome" title={linha.titulo}>{linha.titulo}</span>
                <span style={{ flex: 1 }} />
                <span className="wic__fim">
                {/* bolinha do status, logo antes do calendário: a cor diz o
                    estado sem ocupar largura, e a linha de resumo tem a dela
                    (verde quando tudo dentro do checklist está concluído). */}
                {/* etapa e status direto na linha: mexer aqui evita abrir o
                    item só para mudar uma coisa. Só linha com item de verdade. */}
                {linha.itemId && etapaOpcoes.length > 0 ? (
                  <span className="wic__menuwrap">
                    <button
                      type="button"
                      className="wic__etapabtn"
                      title="Mudar a etapa"
                      aria-label="Mudar a etapa"
                      onClick={(e) => {
                        const anc = e.currentTarget.getBoundingClientRect()
                        e.stopPropagation()
                        setEditando(null)
                        setMenuDe((c) =>
                          c?.chave === linha.chave && c.qual === 'etapa'
                            ? null
                            : { chave: linha.chave, qual: 'etapa', anc },
                        )
                      }}
                    >
                      <i className="fas fa-stream" aria-hidden />
                    </button>
                    {menuDe?.chave === linha.chave && menuDe.qual === 'etapa' ? (
                      <PopFlutuante anc={menuDe.anc} classe="wic__menu" onFechar={() => setMenuDe(null)}>
                        {etapaOpcoes.map((o) => (
                          <button
                            key={o.id}
                            type="button"
                            className="wic__menuopt"
                            onClick={() => {
                              onTrocarEtapa?.(linha.itemId!, o.id)
                              setMenuDe(null)
                            }}
                          >
                            {o.label}
                          </button>
                        ))}
                      </PopFlutuante>
                    ) : null}
                  </span>
                ) : null}
                {linha.itemId && statusOpcoes.length > 0 ? (
                  <span className="wic__menuwrap">
                    <button
                      type="button"
                      className="wic__statusdot wic__statusdot--btn"
                      style={{ background: cor }}
                      title={`${linha.concluido ? 'Concluído' : 'Em aberto'} — clique para mudar`}
                      aria-label="Mudar o status"
                      onClick={(e) => {
                        const anc = e.currentTarget.getBoundingClientRect()
                        e.stopPropagation()
                        setEditando(null)
                        setMenuDe((c) =>
                          c?.chave === linha.chave && c.qual === 'status'
                            ? null
                            : { chave: linha.chave, qual: 'status', anc },
                        )
                      }}
                    />
                    {menuDe?.chave === linha.chave && menuDe.qual === 'status' ? (
                      <PopFlutuante anc={menuDe.anc} classe="wic__menu" onFechar={() => setMenuDe(null)}>
                        {statusOpcoes.map((o) => (
                          <button
                            key={o.id}
                            type="button"
                            className="wic__menuopt"
                            onClick={() => {
                              onTrocarStatus?.(linha.itemId!, o.id)
                              setMenuDe(null)
                            }}
                          >
                            <span
                              className="wic__statusdot"
                              style={{ background: TONE_COLOR[o.tone as keyof typeof TONE_COLOR] ?? 'var(--ob-primary)' }}
                            />
                            {o.label}
                          </button>
                        ))}
                      </PopFlutuante>
                    ) : null}
                  </span>
                ) : (
                  <span
                    className="wic__statusdot"
                    style={{ background: cor }}
                    title={linha.concluido ? 'Concluído' : 'Em aberto'}
                    aria-label={linha.concluido ? 'Concluído' : 'Em aberto'}
                  />
                )}
                {/* o calendário só mexe em data: não abre o item */}
                {podeDatar ? (
                  <button
                    type="button"
                    className={`wic__data${linha.ini ? ' wic__data--on' : ''}`}
                    aria-label={`Definir datas de ${linha.titulo}`}
                    title="Definir início e prazo"
                    onClick={(e) => {
                      e.stopPropagation()
                      const anc = e.currentTarget.getBoundingClientRect()
                      setMenuDe(null)
                      setEditando((c) => (c?.chave === linha.chave ? null : { chave: linha.chave, anc }))
                    }}
                  >
                    <i className="far fa-calendar" aria-hidden />
                  </button>
                ) : null}
                </span>
                {editando?.chave === linha.chave ? (
                  <PopFlutuante anc={editando.anc} classe="wic__datapop" onFechar={() => setEditando(null)}>
                    <label className="wic__datacampo">
                      <span>Início</span>
                      <DateInput
                        value={linha.ini ? new Date(linha.ini) : null}
                        onChange={(d) =>
                          gravar(linha, { startAt: d ? d.toISOString() : null, dueAt: linha.fim })
                        }
                      />
                    </label>
                    <label className="wic__datacampo">
                      <span>Prazo</span>
                      <DateInput
                        value={linha.fim ? new Date(linha.fim) : null}
                        onChange={(d) => gravar(linha, { startAt: linha.ini, dueAt: d ? d.toISOString() : null })}
                      />
                    </label>
                    <button type="button" className="wic__datafechar" onClick={() => setEditando(null)}>
                      Fechar
                    </button>
                  </PopFlutuante>
                ) : null}
              </div>
              <div className="pj__tempo" style={{ width: faixa ? faixa.dias.length * DIA_W : undefined, minWidth: faixa ? undefined : 320 }}>
                {faixa?.dias.map((d, i) => (
                  <span key={i} className={`pj__dia${d.getDay() === 0 || d.getDay() === 6 ? ' pj__dia--fds' : ''}`} style={{ width: DIA_W }} />
                ))}
                {hoje !== null && hoje >= 0 ? <span className="pj__hoje" style={{ left: hoje }} title="Hoje" /> : null}
                {barra ? (
                  <span
                    className={`pj__barra${linha.resumo ? ' pj__barra--resumo' : ''}`}
                    style={{ left: barra.left, width: barra.width, background: cor }}
                    title={`${linha.code ? `${linha.code} — ` : ''}${linha.titulo}`}
                  >
                    {barra.width > 70 ? linha.titulo : ''}
                  </span>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
